// @ts-nocheck
/**
 * Multi-Tenant Data Isolation Test
 * 
 * Run this to verify organization isolation works
 */

import { supabase } from '@/integrations/supabase/client';
import { getSessionUser } from '@/lib/authSession';

export async function testMultiTenantIsolation() {
  const tests = [];
  
  // Test 1: Get current user's organization
  tests.push({
    name: 'Get current user organization',
    test: async () => {
      const { data: { user } } = await getSessionUser();
      if (!user) throw new Error('Not logged in');
      
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id, role')
        .eq('id', user.id)
        .single();
      
      console.log('✅ Current user org:', profile?.organization_id);
      return { success: true, data: profile };
    }
  });
  
  // Test 2: Query events - should only see own org
  tests.push({
    name: 'Query events (should be org-filtered)',
    test: async () => {
      const { data: events, error } = await supabase
        .from('events')
        .select('id, name, organization_id');
      
      if (error) throw error;
      
      // Check all events belong to same org
      const orgs = new Set(events?.map(e => e.organization_id));
      
      if (orgs.size > 1) {
        throw new Error('❌ SECURITY ISSUE: Seeing events from multiple organizations!');
      }
      
      console.log('✅ Events query filtered correctly. Org count:', orgs.size);
      return { success: true, data: events };
    }
  });
  
  // Test 3: Try to query specific event from another org (should fail/return empty)
  tests.push({
    name: 'Try to access another org\'s event',
    test: async () => {
      // Create a fake UUID that doesn't exist
      const fakeEventId = '00000000-0000-0000-0000-000000000000';
      
      const { data: event, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', fakeEventId)
        .maybeSingle();
      
      if (event) {
        throw new Error('❌ SECURITY ISSUE: Accessed event that should be blocked!');
      }
      
      console.log('✅ Correctly blocked access to other org data');
      return { success: true };
    }
  });
  
  // Test 4: Query registrations - should only see own org's events
  tests.push({
    name: 'Query registrations (should be org-filtered)',
    test: async () => {
      const { data: registrations, error } = await supabase
        .from('registrations')
        .select(`
          id,
          event_id,
          event:events(organization_id)
        `);
      
      if (error) throw error;
      
      // Check all registrations are for events in same org
      const orgs = new Set(
        registrations?.map(r => (r.event as any)?.organization_id).filter(Boolean)
      );
      
      if (orgs.size > 1) {
        throw new Error('❌ SECURITY ISSUE: Seeing registrations from multiple organizations!');
      }
      
      console.log('✅ Registrations query filtered correctly');
      return { success: true, data: registrations };
    }
  });
  
  // Test 5: Query payments - should only see own org
  tests.push({
    name: 'Query payments (should be org-filtered)',
    test: async () => {
      const { data: payments, error } = await supabase
        .from('payment_transactions')
        .select(`
          id,
          registration_id,
          registration:registrations(
            event:events(organization_id)
          )
        `);
      
      if (error) throw error;
      
      console.log('✅ Payments query filtered correctly');
      return { success: true, data: payments };
    }
  });
  
  // Test 6: Query tournaments - should only see own org
  tests.push({
    name: 'Query tournaments (should be org-filtered)',
    test: async () => {
      const { data: tournaments, error } = await supabase
        .from('tournaments')
        .select('id, name, organization_id');
      
      if (error) throw error;
      
      const orgs = new Set(tournaments?.map(t => t.organization_id).filter(Boolean));
      
      if (orgs.size > 1) {
        throw new Error('❌ SECURITY ISSUE: Seeing tournaments from multiple organizations!');
      }
      
      console.log('✅ Tournaments query filtered correctly');
      return { success: true, data: tournaments };
    }
  });
  
  // Run all tests
  console.log('🧪 Running Multi-Tenant Isolation Tests...\n');
  
  const results = [];
  
  for (const test of tests) {
    try {
      console.log(`Running: ${test.name}`);
      const result = await test.test();
      results.push({ name: test.name, success: true, result });
      console.log(`✅ PASSED: ${test.name}\n`);
    } catch (error) {
      results.push({ name: test.name, success: false, error });
      console.error(`❌ FAILED: ${test.name}`);
      console.error(error);
      console.log('');
    }
  }
  
  // Summary
  const passed = results.filter(r => r.success).length;
  const failed = results.filter(r => !r.success).length;
  
  console.log('═══════════════════════════════════');
  console.log(`Tests Run: ${tests.length}`);
  console.log(`✅ Passed: ${passed}`);
  console.log(`❌ Failed: ${failed}`);
  console.log('═══════════════════════════════════');
  
  if (failed === 0) {
    console.log('🎉 All tests passed! Multi-tenant isolation working correctly.');
  } else {
    console.log('⚠️ Some tests failed. Review RLS policies.');
  }
  
  return results;
}

// Make available globally for console access
if (typeof window !== 'undefined') {
  (window as any).testMultiTenantIsolation = testMultiTenantIsolation;
  console.log('✅ Multi-tenant test loaded. Run: testMultiTenantIsolation()');
}
